import React, { Component, ReactNode } from 'react'
import ReactDOM from 'react-dom'

let doc: Document | null = null
let win: Window | null = null

export interface FrameContextValue {
  document: Document | null
  window: Window | null
}

export const FrameContext = React.createContext<FrameContextValue>({
  document: doc,
  window: win,
})

export const useFrameContext = () => {
  const context = React.useContext(FrameContext)
  if (!context) {
    throw new Error('useFrameContext must be used within a FrameContextProvider')
  }
  return context
}

export const { Provider: FrameContextProvider, Consumer: FrameContextConsumer } = FrameContext

interface ContentProps {
  contentDidMount: () => void
  children: ReactNode
}

class Content extends Component<ContentProps> {
  componentDidMount() {
    this.props.contentDidMount()
  }

  render() {
    return React.Children.only(this.props.children)
  }
}

export interface FrameProps extends React.ComponentProps<'iframe'> {
  style?: React.CSSProperties
  head?: ReactNode
  initialContent?: string
  mountTarget?: string
  onFrameLoaded?: (document: Document | null) => void
  onContentLoaded?: (document: Document | null) => void
  children?: ReactNode
  forwardedRef?: React.Ref<HTMLIFrameElement>
}

interface FrameState {
  iframeLoaded: boolean
  mountTarget: Element | null
}

/**
 * @note this is a fork from https://github.com/ryanseddon/react-frame-component
 *
 * @todo remove this frame implementation from app builder
 */
export class Frame extends Component<FrameProps, FrameState> {
  static defaultProps: Partial<FrameProps> = {
    style: {},
    head: null,
    children: undefined,
    mountTarget: undefined,
    onFrameLoaded: () => {},
    onContentLoaded: () => {},
    initialContent: '<!DOCTYPE html><html><head></head><body><div class="frame-root"></div></body></html>',
  }

  private _isMounted = false
  private nodeRef: { current: HTMLIFrameElement | null } = { current: null }
  private mountTargetCheckInterval?: NodeJS.Timeout

  constructor(props: FrameProps) {
    super(props)
    this._isMounted = false
    this.state = {
      iframeLoaded: false,
      mountTarget: null,
    }
  }

  componentDidMount() {
    this._isMounted = true
    const doc = this.getDoc()
    if (doc) {
      this.nodeRef.current?.contentWindow?.addEventListener('DOMContentLoaded', this.handleLoad)
    }
    this.checkMountTarget()
  }

  componentDidUpdate() {
    this.checkMountTarget()
  }

  componentWillUnmount() {
    this._isMounted = false
    this.nodeRef.current?.removeEventListener('DOMContentLoaded', this.handleLoad)
    if (this.mountTargetCheckInterval) {
      clearInterval(this.mountTargetCheckInterval)
    }
  }

  checkMountTarget = () => {
    const currentMountTarget = this.getMountTarget()
    if (currentMountTarget && currentMountTarget !== this.state.mountTarget) {
      this.setState({ mountTarget: currentMountTarget })
    } else if (!currentMountTarget && this.state.mountTarget) {
      this.setState({ mountTarget: null })
    }
  }

  handleLoad = () => {
    if (this.mountTargetCheckInterval) {
      clearInterval(this.mountTargetCheckInterval)
    }
    if (!this.state.iframeLoaded) {
      this.setState({ iframeLoaded: true })
      this.mountTargetCheckInterval = setInterval(this.checkMountTarget, 100)
      this.props.onFrameLoaded?.(this.getDoc())
    }
  }

  getDoc(): Document | null {
    return this.nodeRef.current?.contentDocument ?? null
  }

  getMountTarget(): Element | null {
    const doc = this.getDoc()
    if (!doc) return null

    if (this.props.mountTarget) {
      return doc.querySelector(this.props.mountTarget)
    }
    return doc.body?.children?.[0] ?? null
  }

  setRef = (node: HTMLIFrameElement | null) => {
    this.nodeRef.current = node

    const { forwardedRef } = this.props
    if (typeof forwardedRef === 'function') {
      forwardedRef(node)
    } else if (forwardedRef) {
      // @ts-ignore - We know this is safe because we checked the type
      forwardedRef.current = node
    }
  }

  renderFrameContents() {
    if (!this._isMounted || !this.state.iframeLoaded) {
      return null
    }

    const doc = this.getDoc()

    if (!doc) {
      return null
    }

    const win = doc.defaultView || window
    const contents = (
      <Content contentDidMount={() => this.props.onContentLoaded?.(doc)}>
        <FrameContextProvider value={{ document: doc, window: win }}>
          <div className='frame-content'>{this.props.children}</div>
        </FrameContextProvider>
      </Content>
    )

    const { mountTarget } = this.state
    const head = doc.head

    const headPortal = head ? ReactDOM.createPortal(this.props.head, head) : null
    const bodyPortal = mountTarget ? ReactDOM.createPortal(contents, mountTarget) : null

    return [headPortal, bodyPortal].filter(Boolean)
  }

  render() {
    const props = {
      ...this.props,
      srcDoc: this.props.initialContent,
      children: undefined,
    }
    delete props.head
    delete props.initialContent
    delete props.mountTarget
    delete props.onContentLoaded
    delete props.onFrameLoaded
    delete props.forwardedRef

    return (
      <iframe {...props} ref={this.setRef} onLoad={this.handleLoad}>
        {this.renderFrameContents()}
      </iframe>
    )
  }
}
